import React from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { format } from 'date-fns';
import { Download, Pencil, Plus } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { useBudgetReport } from '@/services/budgetService';
import { formatCalendarDate, formatCurrency } from '@/utils/formatters';
import { downloadCsv } from '@/utils/csv';
import { BudgetMeter } from '@/components/dashboard/BudgetMeter';
import { BudgetEditDialog } from '@/components/dashboard/BudgetEditDialog';
import type { BudgetReportRow } from '@/types';

/** The calendar day before an exclusive UTC-midnight bound, as yyyy-MM-dd. */
function dayBefore(iso: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!match) return '';
  return new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]) - 1)).toISOString().slice(0, 10);
}

/** A signed amount; negatives (over budget) in the destructive colour. Absent means no budget. */
const Signed: React.FC<{ value?: number; testId?: string }> = ({ value, testId }) =>
  value === undefined ? (
    <span className="text-grey-400" data-testid={testId}>{'—'}</span>
  ) : (
    <span className={cn('tabular-nums', value < 0 && 'text-destructive font-semibold')} data-testid={testId}>
      {formatCurrency(value)}
    </span>
  );

const CSV_HEADERS = ['Department', 'Code', 'Budget', 'Committed', 'Remaining', 'Committed %', 'Actual', 'Variance', 'Notes'];

/**
 * Budgets page (#19): budget, committed, remaining, actual and variance per department for one
 * fiscal year (`/budgets?fiscalYear=2026`; without it, the current fiscal year the API answers
 * with). Admin and Manager set, change or delete a department's budget from its row; everyone
 * reads. Contracts and costs without a department are in the Unassigned row.
 */
const Budgets: React.FC = () => {
  const navigate = useNavigate();
  const { fiscalYear: requestedYear } = useSearch({ from: '/budgets' });
  const { canManageBudgets } = useAuth();
  const { data: report, isLoading, isPlaceholderData, error } = useBudgetReport(requestedYear);
  const isStale = isPlaceholderData;
  // The dialog is bound to the fiscal year it was opened for; a change of year (select, Back,
  // Forward) closes it, so a save can never move a budget to the year now on screen
  const [editing, setEditing] = React.useState<{ row: BudgetReportRow; fiscalYear: number; label: string } | null>(null);
  if (editing && report && !isStale && editing.fiscalYear !== report.fiscalYear) {
    setEditing(null);
  }
  if (editing && requestedYear !== undefined && editing.fiscalYear !== requestedYear) {
    setEditing(null);
  }

  // The current fiscal year comes from the API (it knows App:FiscalYearStartMonth); the same query
  // backs the dashboard card, so it is usually cached
  const { data: current } = useBudgetReport();
  const year = requestedYear ?? report?.fiscalYear;
  const anchor = current?.fiscalYear ?? new Date().getUTCFullYear();
  // Two years back and one ahead of the current one, plus the requested year when it is outside that range
  const years = Array.from(new Set([anchor - 2, anchor - 1, anchor, anchor + 1, ...(year !== undefined ? [year] : [])])).sort((a, b) => a - b);

  const setYear = (value: string) => {
    const parsed = Number(value);
    navigate({ to: '/budgets', search: (prev) => ({ ...prev, fiscalYear: parsed }) });
  };

  const handleExport = () => {
    if (!report || isStale) return;
    const rows = [...report.rows, report.totals].map((row) => [
      row.departmentName,
      row.departmentCode ?? '',
      row.budget ?? '',
      row.committed,
      row.remaining ?? '',
      row.committedPercent ?? '',
      row.actual,
      row.variance ?? '',
      row.budgetNotes ?? '',
    ]);
    downloadCsv(`budgets-FY${report.fiscalYear}-${format(new Date(), 'yyyy-MM-dd')}.csv`, CSV_HEADERS, rows);
  };

  // Committed spend in rows without a budget (Unassigned included) still counts against the total budget
  const unbudgetedCommitted = (report?.rows ?? []).filter((r) => r.budget === undefined).reduce((sum, r) => sum + r.committed, 0);

  const renderRow = (row: BudgetReportRow, isTotal = false) => {
    const isUnassigned = !isTotal && !row.departmentId;
    const testId = isTotal ? 'budget-row-total' : isUnassigned ? 'budget-row-unassigned' : `budget-row-${row.departmentId}`;
    return (
      <TableRow key={testId} data-testid={testId} className={cn(isTotal && 'font-semibold bg-grey-50', isUnassigned && 'text-grey-600')}>
        <TableCell className="min-w-[160px]">
          <span className={cn(!isTotal && 'font-medium')}>{row.departmentName}</span>
          {row.departmentCode && <span className="ml-2 text-xs text-grey-500">{row.departmentCode}</span>}
          {!row.isActive && <span className="ml-2 text-xs text-grey-500">(inactive)</span>}
          {row.budgetNotes && <span className="block text-xs text-grey-500 break-words">{row.budgetNotes}</span>}
          {isTotal && unbudgetedCommitted > 0 && (
            <span className="block text-xs font-normal text-grey-500 break-words" data-testid="budget-total-note">
              Includes {formatCurrency(unbudgetedCommitted)} committed with no budget
            </span>
          )}
        </TableCell>
        <TableCell className="text-right whitespace-nowrap">
          {row.budget !== undefined ? (
            <span className="tabular-nums">{formatCurrency(row.budget)}</span>
          ) : (
            <span className="text-grey-400">No budget</span>
          )}
        </TableCell>
        <TableCell className="text-right whitespace-nowrap tabular-nums">
          <span data-testid="budget-committed">{formatCurrency(row.committed)}</span>
          <span className="block text-xs text-grey-500">
            {row.committedContractCount} {row.committedContractCount === 1 ? 'contract' : 'contracts'}
          </span>
        </TableCell>
        <TableCell className="text-right whitespace-nowrap">
          <Signed value={row.remaining} testId="budget-remaining" />
        </TableCell>
        <TableCell>
          <BudgetMeter percent={row.committedPercent} />
        </TableCell>
        <TableCell className="text-right whitespace-nowrap tabular-nums">{formatCurrency(row.actual)}</TableCell>
        <TableCell className="text-right whitespace-nowrap">
          <Signed value={row.variance} testId="budget-variance" />
        </TableCell>
        {canManageBudgets && (
          <TableCell className="text-right">
            {!isTotal && !isUnassigned && (
              <Button
                variant="ghost"
                size="sm"
                disabled={isStale}
                onClick={() => report && setEditing({ row, fiscalYear: report.fiscalYear, label: report.fiscalYearLabel })}
                aria-label={`${row.budgetId ? 'Edit budget' : 'Set budget'} for ${row.departmentName}`}
              >
                {row.budgetId ? <Pencil className="h-4 w-4" aria-hidden="true" /> : <Plus className="h-4 w-4" aria-hidden="true" />}
                <span className="ml-1">{row.budgetId ? 'Edit' : 'Set'}</span>
              </Button>
            )}
          </TableCell>
        )}
      </TableRow>
    );
  };

  return (
    <div className="max-w-screen-xl mx-auto px-4 py-8">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-semibold text-grey-800">Budgets</h1>
          <p className="text-sm text-grey-500 mt-1">
            Budget versus committed and actual spend by department
            {report && !isStale
              ? `, fiscal year ${report.fiscalYearLabel} (${formatCalendarDate(report.yearStart)} to ${formatCalendarDate(dayBefore(report.yearEnd))})`
              : ''}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Select value={year !== undefined ? String(year) : undefined} onValueChange={setYear}>
            <SelectTrigger className="w-[140px]" aria-label="Fiscal year" data-testid="budgets-year">
              <SelectValue placeholder="Fiscal year" />
            </SelectTrigger>
            <SelectContent>
              {years.map((option) => (
                <SelectItem key={option} value={String(option)}>
                  FY {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={handleExport} disabled={!report || isStale} data-testid="budgets-export">
            <Download className="h-4 w-4 mr-2" aria-hidden="true" />
            Export CSV
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>Failed to load the budget report: {error.message}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Departments</CardTitle>
          <CardDescription>
            Committed: in-force contracts&apos; annual value for the months of their term in the year (partial months
            by days), by contract department; a July-to-June contract counts half in each year. Actual: cost
            records dated in the year. Remaining: budget minus committed. Variance: budget minus actual.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-4 space-y-2">
              <Skeleton className="h-[48px] w-full" />
              <Skeleton className="h-[48px] w-full" />
              <Skeleton className="h-[48px] w-full" />
            </div>
          ) : report ? (
            <div className={cn('overflow-x-auto contain-inline-size', isStale && 'opacity-60')} aria-busy={isStale}>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Department</TableHead>
                    <TableHead className="text-right">Budget</TableHead>
                    <TableHead className="text-right">Committed</TableHead>
                    <TableHead className="text-right">Remaining</TableHead>
                    <TableHead>Committed %</TableHead>
                    <TableHead className="text-right">Actual</TableHead>
                    <TableHead className="text-right">Variance</TableHead>
                    {canManageBudgets && <TableHead className="text-right"><span className="sr-only">Actions</span></TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {report.rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={canManageBudgets ? 8 : 7} className="text-center text-grey-500 py-6">
                        No departments
                      </TableCell>
                    </TableRow>
                  ) : (
                    <>
                      {report.rows.map((row) => renderRow(row))}
                      {renderRow(report.totals, true)}
                    </>
                  )}
                </TableBody>
              </Table>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <BudgetEditDialog
        row={editing?.row ?? null}
        fiscalYear={editing?.fiscalYear ?? 0}
        fiscalYearLabel={editing?.label ?? ''}
        onClose={() => setEditing(null)}
      />
    </div>
  );
};

export default Budgets;
